import { ReactNode, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import { ObjectDetailsModel } from "../../../core/api/objects/types";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";
import { userDateFormat } from "../../../ui-lib/utils/userDateFormat";
import useUser from "../../../core/user/useUser";
import useLanguage from "../../../core/i18n/useLanguage";
import useTranslations from "../../../core/i18n/useTranslations";
import CollapsibleBlock from "../../../ui-lib/components/CollapsibleBlock/CollapsibleBlock";
import DatePicker from "../../../ui-lib/components/DateAndTime/DatePicker";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import Divider from "../../../ui-lib/components/Divider/Divider";
import useSharedObject from "../SharedObjectContext/useSharedObject";
import Autocomplete from "../../../ui-lib/components/Autocomplete/Autocomplete";

import styles from "./ObjectGeneral.module.css";

const DesktopReferralWrapper = ({ children }: { children: ReactNode }) => {
  const t = useTranslations();
  return (
    <div className={styles.referralWrapper}>
      <Spacer size={32} />
      <Divider />
      <Spacer size={32} />
      <CollapsibleBlock withScroll title={t("Objects:referral_info_title")}>
        {children}
      </CollapsibleBlock>
    </div>
  );
};

const MobileReferralWrapper = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

const ReferralInfoContent = () => {
  const t = useTranslations();
  const { objectFields } = useSharedObject();
  const { currentLanguage } = useLanguage();
  const { config } = useUser();
  const { control, watch } = useFormContext<ObjectDetailsModel>();
  const selectedUrgencyOfReferral = watch("urgencyOfReferral");
  const dateFormat = useMemo(
    () =>
      config?.dateFormat ? userDateFormat(config.dateFormat) : "DD-MM-YYYY",
    [],
  );
  const urgencyOfReferralOptions: IItemProp[] = useMemo(() => {
    return [
      {
        id: "1",
        name: t("Objects:priority_1"),
        isSelected: selectedUrgencyOfReferral === "1",
      },
      {
        id: "2",
        name: t("Objects:priority_2"),
        isSelected: selectedUrgencyOfReferral === "2",
      },
      {
        id: "3",
        name: t("Objects:priority_3"),
        isSelected: selectedUrgencyOfReferral === "3",
      },
      {
        id: "4",
        name: t("Objects:priority_4"),
        isSelected: selectedUrgencyOfReferral === "4",
      },
    ];
  }, [selectedUrgencyOfReferral, currentLanguage]);

  return (
    <>
      <Spacer size={32} />
      <h2>{t("Objects:referral_info_title")}</h2>
      <p>{t("Objects:referral_info_description")}</p>
      <Spacer size={16} />
      <TwoColGrid>
        <Controller
          name="dateOfReferral"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:dateOfReferral_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        <Controller
          name="dateCompletedReferralReceived"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:dateCompletedReferralReceived_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Autocomplete
          fieldData={objectFields?.sourceReferral}
          name="sourceReferral"
          defaultLabel={t("Objects:sourceReferral_label")}
          defaultPlaceholder={t("Objects:sourceReferral_placeholder")}
        />
        <Autocomplete
          fieldData={objectFields?.factorsDelayedReferral}
          name="factorsDelayedReferral"
          defaultLabel={t("Objects:factorsDelayedReferral_label")}
          defaultPlaceholder={t("Objects:factorsDelayedReferral_placeholder")}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        {objectFields?.urgencyOfReferral ? (
          <Autocomplete
            fieldData={objectFields?.urgencyOfReferral}
            name="urgencyOfReferral"
            defaultLabel={t("Objects:urgencyOfReferral_label")}
            defaultPlaceholder={t("Objects:urgencyOfReferral_placeholder")}
          />
        ) : (
          <Controller
            name="urgencyOfReferral"
            control={control}
            render={({ field }) => (
              <Dropdown
                withClearItem
                title={t("Objects:urgencyOfReferral_label")}
                placeholder={t("Objects:urgencyOfReferral_placeholder")}
                selectedItem={urgencyOfReferralOptions.find(
                  (i) => i.isSelected,
                )}
                items={urgencyOfReferralOptions}
                onSelectItem={(o) => field.onChange(o.id === -1 ? null : o.id)}
              />
            )}
          />
        )}
        <Autocomplete
          fieldData={objectFields?.reasonReferral}
          name="reasonReferral"
          defaultLabel={t("Objects:reasonReferral_label")}
          defaultPlaceholder={t("Objects:reasonReferral_placeholder")}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Autocomplete
          fieldData={objectFields?.modeReferral}
          name="modeReferral"
          defaultLabel={t("Objects:modeReferral_label")}
          defaultPlaceholder={t("Objects:modeReferral_placeholder")}
        />
        <Autocomplete
          fieldData={objectFields?.typeReferral}
          name="typeReferral"
          defaultLabel={t("Objects:typeReferral_label")}
          defaultPlaceholder={t("Objects:typeReferral_placeholder")}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Controller
          name="assessmentCompletionDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:assessmentCompletionDate_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        <Autocomplete
          fieldData={objectFields?.outcomeReferral}
          name="outcomeReferral"
          defaultLabel={t("Objects:outcomeReferral_label")}
          defaultPlaceholder={t("Objects:outcomeReferral_placeholder")}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Autocomplete
          fieldData={objectFields?.reasonDelayedInstallation}
          name="reasonDelayedInstallation"
          defaultLabel={t("Objects:reasonDelayedInstallation_label")}
          defaultPlaceholder={t(
            "Objects:reasonDelayedInstallation_placeholder",
          )}
        />
        <Autocomplete
          fieldData={objectFields?.serviceType}
          name="serviceType"
          defaultLabel={t("Objects:serviceType_label")}
          defaultPlaceholder={t("Objects:serviceType_placeholder")}
        />
      </TwoColGrid>

      <Spacer size={32} />

      <TwoColGrid>
        <Controller
          name="installationDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:installationDate_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        <Controller
          name="dateNotificationReferralOtherAgencies"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:onwardNotificationDate_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Autocomplete
          fieldData={objectFields?.referralOtherAgency}
          name="referralOtherAgency"
          defaultLabel={t("Objects:referralOtherAgency_label")}
          defaultPlaceholder={t("Objects:referralOtherAgency_placeholder")}
        />
        <Autocomplete
          fieldData={objectFields?.typeServiceReferred}
          name="typeServiceReferred"
          defaultLabel={t("Objects:typeServiceReferred_label")}
          defaultPlaceholder={t("Objects:typeServiceReferred_placeholder")}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Autocomplete
          fieldData={objectFields?.reasonOnwardReferral}
          name="reasonOnwardReferral"
          defaultLabel={t("Objects:reasonOnwardReferral_label")}
          defaultPlaceholder={t("Objects:reasonOnwardReferral_placeholder")}
        />
      </TwoColGrid>

      <Spacer size={32} />

      <TwoColGrid>
        <Controller
          name="reviewUserBefore"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:reviewUserBefore_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        <Autocomplete
          fieldData={objectFields?.outcomeReview}
          name="outcomeReview"
          defaultLabel={t("Objects:outcomeReview_label")}
          defaultPlaceholder={t("Objects:outcomeReview_placeholder")}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <TwoColGrid>
        <Controller
          name="serviceWithdrawalDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t("Objects:serviceWithdrawalDate_label")}
              placeholder={dateFormat}
              value={field.value ? new Date(field.value) : undefined}
              onChange={field.onChange}
              dateFormat={dateFormat}
            />
          )}
        />
        <Autocomplete
          fieldData={objectFields?.withdrawalReason}
          name="withdrawalReason"
          defaultLabel={t("Objects:withdrawalReason_label")}
          defaultPlaceholder={t("Objects:withdrawalReason_placeholder")}
        />
      </TwoColGrid>
    </>
  );
};

export const ReferralInfo = () => {
  return (
    <MobileReferralWrapper>
      <ReferralInfoContent />
    </MobileReferralWrapper>
  );
};
